import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { InitializerService } from './services/initializer.service';
// import {
//   getAnalytics,
//   provideAnalytics,
//   ScreenTrackingService,
//   UserTrackingService,
// } from '@angular/fire/analytics';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { EnvironmentProviders, isDevMode, Provider } from '@angular/core';
import {
  Auth,
  connectAuthEmulator,
  getAuth,
  provideAuth,
} from '@angular/fire/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  provideFirestore,
} from '@angular/fire/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  provideFunctions,
} from '@angular/fire/functions';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import {
  getRemoteConfig,
  provideRemoteConfig,
} from '@angular/fire/remote-config';
import {
  connectStorageEmulator,
  getStorage,
  provideStorage,
} from '@angular/fire/storage';
import { UnaryRequest } from '@connectrpc/connect';
import {
  NbAuthJWTInterceptor,
  NbAuthModule,
  NbAuthService,
} from '@nebular/auth';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import {
  NbDialogModule,
  NbLayoutModule,
  NbMenuModule,
  NbMenuService,
  NbSidebarService,
  NbThemeModule,
  NbToastrModule,
  NbWindowModule,
} from '@nebular/theme';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { ActionReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as firebaseConfig from '@vmspdb/firebase-config';
import { createNgrxSanitizers } from '@vmspdb/ui/common';
import { INTERCEPTORS, provideConnect } from '@vmspdb/ui/connect';
import { AuthStrategies } from '@vmspdb/ui/domain';
import {
  NbFirebaseAuthModule,
  NbFirebaseGoogleStrategy,
  NbFirebasePasswordStrategy,
} from '@vmspdb/ui/firebase-auth';
import { CommonStoreModule } from '@vmspdb/ui/store';
import * as LogRocket from 'logrocket';
import createNgrxMiddleware from 'logrocket-ngrx';
import { localStorageSync } from 'ngrx-store-localstorage';
import { environment } from '../environments/environment';
import { GlobalModule } from './global/global.module';
import { getAppName } from './shared/utils';
import { FrontendStoreModule } from './store/frontend-store.module';
import { loadProjects } from './store/projects';

const { emulators } = firebaseConfig;

/// Firebase
export const firebaseServices = []; // = [ScreenTrackingService, UserTrackingService];

export const firebaseProviders = [
  provideFirebaseApp(() => {
    return initializeApp(InitializerService.environment().firebase);
  }),
  // provideAnalytics(() => getAnalytics()),
  provideAuth(() => {
    const auth = getAuth();
    if (environment.useEmulators) {
      connectAuthEmulator(auth, `http://localhost:${emulators.auth.port}`, {
        disableWarnings: true,
      });
    }
    return auth;
  }),
  provideFunctions(() => {
    const functions = getFunctions();
    if (environment.useEmulators) {
      connectFunctionsEmulator(
        functions,
        'localhost',
        emulators.functions.port,
      );
    }
    return functions;
  }),
  provideFirestore(() => {
    const firestore = getFirestore();
    if (environment.useEmulators) {
      connectFirestoreEmulator(
        firestore,
        'localhost',
        emulators.firestore.port,
      );
    }
    return firestore;
  }),
  providePerformance(() => getPerformance()),
  provideRemoteConfig(() => getRemoteConfig()),
  provideStorage(() => {
    const storage = getStorage();
    if (environment.useEmulators) {
      connectStorageEmulator(storage, 'localhost', emulators.storage.port);
    }
    return storage;
  }),
];

/// Nebular
export const nebularServices: (Provider | EnvironmentProviders)[] = [
  NbAuthService,
  NbMenuService,
  NbSidebarService,
  { provide: HTTP_INTERCEPTORS, useClass: NbAuthJWTInterceptor, multi: true },
];

export const nebularModules = [
  NbThemeModule.forRoot({ name: 'default' }),
  NbLayoutModule,
  NbEvaIconsModule,
  NbMenuModule.forRoot(),
  NbToastrModule.forRoot({
    duration: 10000,
  }),
  NbFirebaseAuthModule,
  NbAuthModule.forRoot({
    strategies: [
      NbFirebasePasswordStrategy.setup({
        name: AuthStrategies.Password,
        // token: {
        //   class: NbAuthJWTToken,
        // },
        login: {
          redirect: {
            success: '/',
          },
        },
        // docs/e2e-backlog.md#bug-026 / #gap-062 - /auth/register
        // (NbRegisterComponent) was removed: an unauthenticated
        // self-registration route against an API that accepts any Firebase
        // account, with no invitation/allow-list/admin step of its own. The
        // product provisions users through UserService.Create and the
        // management screens instead.
        logout: {
          redirect: {
            success: 'auth/login',
          },
        },
        requestPassword: {
          redirect: {
            success: 'auth/login',
          },
        },
        resetPassword: {
          redirect: {
            success: 'auth/login',
          },
        },
      }),
      NbFirebaseGoogleStrategy.setup({
        name: AuthStrategies.Google,
        customParameters: {
          app: 'dashboard',
        },
      }),
    ],
    forms: {
      login: {
        strategy: AuthStrategies.Password,
        rememberMe: true,
        socialLinks: [],
      },
      logout: {
        strategy: AuthStrategies.Password,
      },
      requestPassword: {
        strategy: AuthStrategies.Password,
        socialLinks: [],
      },
      resetPassword: {
        strategy: AuthStrategies.Password,
        socialLinks: [],
      },
    },
  }),
  NbWindowModule.forRoot({
    buttons: {
      fullScreen: false,
      maximize: false,
      minimize: false,
    },
    closeOnBackdropClick: false,
  }),
  NbDialogModule.forRoot({
    closeOnBackdropClick: false,
  }),
];

/// Store
const logrocketMiddleware = createNgrxMiddleware(
  LogRocket,
  createNgrxSanitizers({
    actionSanitizer: (action: any) => {
      if (action.type === loadProjects.type) {
        return {
          ...action,
          count: action.projects?.length ?? 0,
          projects: undefined,
        };
      }
      return action;
    },
  }),
);
function localStorageSyncReducer(
  reducer: ActionReducer<any>,
): ActionReducer<any> {
  return localStorageSync({
    keys: ['auth', 'debug', { settings: ['filters'] }],
    rehydrate: true,
  })(reducer);
}
export const storeModules = [
  StoreModule.forRoot(
    {},
    {
      metaReducers: [localStorageSyncReducer, logrocketMiddleware],
      runtimeChecks: {
        strictActionImmutability: true,
        strictStateImmutability: true,
      },
    },
  ),
  EffectsModule.forRoot([]),
  StoreRouterConnectingModule.forRoot(),
  StoreDevtoolsModule.instrument({
    name: `VMS Frontend ${getAppName()}`,
    maxAge: isDevMode() ? false : 25,
    logOnly: !isDevMode(),
    autoPause: !isDevMode(),
    trace: isDevMode(),
    features: {
      jump: isDevMode(),
      skip: isDevMode(),
      reorder: isDevMode(),
    },
    connectInZone: true,
    serialize: {
      replacer: (_key, value) =>
        typeof value === 'bigint' ? value.toString() : value,
    },
  }),
  CommonStoreModule,
  FrontendStoreModule,
];

export const otherModules = [
  GlobalModule,
  // FullCalendarModule, // for full calendar
];

export const connectProviders: (Provider | EnvironmentProviders)[] = [
  provideConnect({
    baseUrl: '/api',
  }),
  {
    provide: INTERCEPTORS,
    multi: true,
    useFactory: makeAuthInterceptor,
    deps: [Auth],
  },
];

// Auth interceptor for injecting a bearer token
function makeAuthInterceptor(auth: Auth) {
  return (next: any) => async (req: UnaryRequest) => {
    const token = (await auth.currentUser?.getIdToken()) ?? '';
    req.header.set('Authorization', `Bearer ${token}`);
    return await next(req);
  };
}
